import { I18n } from '@lingui/core';
import upperFirst from 'lodash/upperFirst';

import { EDate } from './EDate';
import { getLocaleForLang, getLocalI18n } from './i18n';
import { YMD } from './YMD';

import { LocalTimeZone } from '../../../utils/LocalTimeZone';
import { logger } from '../../../utils/logger';
import type {
  BlackoutHoliday,
  BlackoutPeriod,
  EwebinarSessions,
  ScheduledWebinar,
  ScheduleSettings,
  Session,
} from '../../typesProxy';
import {
  SessionType,
  SessionStatus,
  getLocale,
  ScheduleTypes,
  RecurringTypes,
  DayOfMonthTypes,
} from '../../typesProxy';
import { EwebinarFragmentForSessions, ScheduledWebinarForSessions } from '../types';

export type ServerDateNow = undefined | (() => Date);
export const LastTimezoneOnEarth = 'Etc/GMT+12';

function nullableCheck<T>(value: T | undefined | null, ifNull: T): T {
  if (value === null || value === undefined) return ifNull;
  return value;
}

function dayOfMonthToNumber(day: DayOfMonthTypes): number {
  const nthDays = [
    DayOfMonthTypes.First,
    DayOfMonthTypes.Second,
    DayOfMonthTypes.Third,
    DayOfMonthTypes.Fourth,
    DayOfMonthTypes.Last,
  ];
  return nthDays.indexOf(day);
}

function determineSpecificDate(date: string | Date, timezone: string): EDate {
  const atDate = new YMD(date, 'UTC');
  return atDate.toEDate(timezone, 0, 0, 0, 0).setSeconds(0);
}

function sortSchedulesByDates(
  schedules: ScheduledWebinar[],
  webinarTimezone: string
): ScheduledWebinarForSessions[] {
  return schedules
    .reduce((combinedSchedules: ScheduledWebinarForSessions[], schedule) => {
      const atDate = schedule?.atDate
        ? determineSpecificDate(schedule.atDate, webinarTimezone)
        : undefined;
      const searchIndex = combinedSchedules.findIndex(
        (s) => s.atDate && atDate && s.atDate.isSameDay(atDate)
      );
      if (searchIndex !== -1) {
        combinedSchedules[searchIndex].atMinutes = (
          combinedSchedules[searchIndex].atMinutes ?? []
        ).concat(schedule.atMinutes ?? []);
      } else {
        combinedSchedules.push({
          ...schedule,
          atDate,
          atMinutes: schedule.atMinutes ?? [],
        });
      }
      return combinedSchedules;
    }, [])
    .sort((a, b) => {
      if (a.atDate && b.atDate) return a.atDate.valueOf() - b.atDate.valueOf();
      return 0;
    });
}

// Allow attendees register within 5 minutes
const ALLOW_REGISTER_WITHIN_MINUTES = -5;

const ALL_WEEKDAYS = [true, true, true, true, true, true, true];

export class SessionCalculator {
  protected startTime: EDate;
  protected endTime: EDate;
  protected locale?: string;
  protected webinarTimezone: string;
  protected scheduleSettings: ScheduleSettings;
  protected localeLoaded = false;
  protected i18n?: I18n;

  protected scheduleType: ScheduleTypes;
  protected schedules: ScheduledWebinarForSessions[];

  public constructor(
    protected ewebinar: EwebinarFragmentForSessions,
    protected clientTimezone: string,
    protected serverDateNow?: ServerDateNow
  ) {
    this.clientTimezone = clientTimezone ? clientTimezone.trim() : LocalTimeZone.name;

    this.webinarTimezone = ewebinar.timeZone === 'client' ? this.clientTimezone : ewebinar.timeZone;
    this.scheduleSettings = ewebinar.scheduleSettings;

    this.scheduleType = ewebinar.scheduleType;

    switch (ewebinar.scheduleType) {
      case ScheduleTypes.OneTime:
        // startDate and endDate are one date
        this.schedules = [
          {
            atDate: ewebinar.startDate
              ? determineSpecificDate(ewebinar.startDate, this.webinarTimezone)
              : undefined,
            atMinutes: ewebinar.scheduleSettings.schedules
              ? [...(ewebinar.scheduleSettings.schedules[0]?.atMinutes ?? [])]
              : [],
          },
        ];
        break;
      case ScheduleTypes.SpecificDateTimes:
        // startDate is the first date and endDate is the last date of the schedules array
        this.schedules = sortSchedulesByDates(
          [...(ewebinar.scheduleSettings.schedules ?? [])],
          this.webinarTimezone
        );
        break;
      case ScheduleTypes.OnDemandOnly:
        this.schedules = [
          {
            onWeekDays: ALL_WEEKDAYS,
            atMinutes: [],
          },
        ];
        break;
      default: {
        // Apollo Client v3 do not allow mutate data so we have to clone ewebinar.scheduleSettings.schedules
        const webinarSchedules = [...(ewebinar.scheduleSettings.schedules ?? [])];
        const specificDates = webinarSchedules.find(
          (s) => s.recurringType === RecurringTypes.SpecificDate
        );
        this.schedules = Boolean(specificDates)
          ? sortSchedulesByDates(webinarSchedules, this.webinarTimezone)
          : (webinarSchedules as ScheduledWebinarForSessions[]);
      }
    }

    // Calc startTime
    /*logger.debug(
      'SESSIONS',
      `ewebinar.startDate ${ewebinar.startDate?.toString()} ${typeof ewebinar.startDate}`
    );
    */

    // Calc startTime
    this.startTime = this.determineStartTime();
    // logger.debug('SESSIONS', `START: ${this.startTime?.toString()}`);

    // Calc endTime
    this.endTime = this.determineEndTime();
    // logger.debug('SESSIONS', `END: ${this.endTime?.toString()}`);
  }

  public now(): Date {
    return this.serverDateNow?.() ?? new Date(Date.now());
  }

  public nowEDate(): EDate {
    return new EDate(this.now(), this.webinarTimezone);
  }

  public determineStartTime(getNowIfStarted = true): EDate {
    let startTime = this.getWebinarStartTime().setSeconds(0);
    const now = this.nowEDate().setSeconds(0);

    if (startTime.isBefore(now) && getNowIfStarted) {
      startTime = now;
    }

    return startTime;
  }

  private getWebinarStartTime(): EDate {
    if (!this.ewebinar.startDate) {
      return this.nowEDate();
    }

    const firstSession = this.determineFirstSession();
    if (firstSession instanceof EDate) {
      return firstSession;
    }

    const atMinutes = firstSession ?? 0;
    const startDate = new YMD(this.ewebinar.startDate, 'UTC');
    const startTime = startDate.toEDate(
      this.webinarTimezone,
      Math.floor(atMinutes / 60),
      Math.floor(atMinutes % 60),
      0,
      0
    );

    return startTime;
  }

  public determineEndTime(): EDate {
    // Default is 1 year from now
    let endTime = this.nowEDate().addDays(365);

    if (this.ewebinar.endDateEnabled && this.ewebinar.endDate) {
      const endDate = new YMD(this.ewebinar.endDate, 'UTC');
      endTime = endDate.toEDate(this.webinarTimezone, 23, 59, 59, 999);
    }
    return endTime;
  }

  private determineFirstSession(): EDate | number {
    if (!this.schedules || !this.schedules[0]?.atMinutes) return 0;

    // Specific Dates and Times or One-time event
    if ([ScheduleTypes.SpecificDateTimes, ScheduleTypes.OneTime].includes(this.scheduleType)) {
      return this.schedules[0].atMinutes[0] ?? 0;
    }

    if (
      this.schedules.length === 1 &&
      this.schedules[0]?.recurringType === RecurringTypes.Everyday
    ) {
      return this.schedules[0].atMinutes[0] ?? 0;
    }

    // Find the first recurring session
    return this.determineFirstRecurringSession();
  }

  private determineFirstRecurringSession(): EDate | number {
    const now = this.nowEDate().setSeconds(0);
    // calc the first session from startDate of webinar
    const endTime = this.determineEndTime();
    if (endTime.isBefore(now)) return 0;

    let webinarStartTime = now;
    if (this.ewebinar.startDate) {
      const startDate = new YMD(this.ewebinar.startDate, 'UTC');
      webinarStartTime = startDate.toEDate(this.webinarTimezone, 0, 0, 0, 0);
      // calc the first recurring session from 7 days ago.
      // some webinars have startDate started some days ago. But the first session will be start today (6PM)
      // Detect first session to make JIT, On-demand, and replay sessions are correct
      // (On-demand, just-in-time sessions, and replays are not available to sign up for until the time your first session is set to begin)
      const last7Days = now.subtractDays(7);
      if (webinarStartTime.isBefore(last7Days)) {
        return 0;
      }
    }

    const sessions: EDate[] = [];
    let d = webinarStartTime;
    while (sessions.length < 1 && d.isSameOrBefore(endTime)) {
      // Convert mon = 0, sun = 6;
      const weekday = d.getDay();

      let atSortedMinutes: number[] = [];

      // 1. Check day valid and combine atMinutes in a day
      for (const schedule of this.schedules) {
        const onWeekDays = schedule.onWeekDays || [];
        if (!schedule.atMinutes || schedule.atMinutes.length === 0) {
          continue;
        }
        if (onWeekDays[weekday]) {
          // 1.1 OnceAMonth
          if (schedule.recurringType === RecurringTypes.OnceAMonth && schedule.nthDayOfMonth) {
            const nthDayOfmonth = dayOfMonthToNumber(schedule.nthDayOfMonth);
            const date = d.getNthDayOfMonth(nthDayOfmonth, weekday);
            if (!date || !d.isSameDay(date)) continue;
          }
          // 1.2 EveryTwoWeek1: Every 2 weeks (odd)
          if (schedule.recurringType === RecurringTypes.EveryTwoWeek1) {
            const isEveryTwoWeek1 = d.isValidEveryTwoWeeks(webinarStartTime);
            if (!isEveryTwoWeek1) continue;
          }
          // 1.3 EveryTwoWeek2: Every 2 weeks (even)
          if (schedule.recurringType === RecurringTypes.EveryTwoWeek2) {
            const isEveryTwoWeek2 = d.isValidEveryTwoWeeks(webinarStartTime.addDays(7));
            if (!isEveryTwoWeek2) continue;
          }
          // 1.4 SpecificDate
          if (schedule.recurringType === RecurringTypes.SpecificDate) {
            if (!schedule.atDate || !d.isSameDay(schedule.atDate)) {
              continue;
            }
          }
          // 1.5 Combine atMinutes
          atSortedMinutes = [...new Set([...atSortedMinutes, ...schedule.atMinutes])];
        }
      }
      // 2. sort atMinutes in a day
      atSortedMinutes = atSortedMinutes.sort((a, b) => a - b);
      // 3. calc the sessions from atSortedMinutes of the day
      for (const atMinutes of atSortedMinutes) {
        const session = d
          .setHours(Math.floor(atMinutes / 60))
          .setMinutes(Math.floor(atMinutes % 60));
        // filter passed or blackout session
        if (!this.inBlackoutRanges(session)) {
          sessions.push(session);
          break;
        }
      }

      const nextD = d.addDays(1);
      if (nextD.getTime() === d.getTime()) {
        throw new Error(
          `determineFirstSession - Error in addDays ${d.date.toString()} ${d.timezone}`
        );
      }
      d = nextD;
    }

    return sessions[0] ?? 0;
  }

  public async setupLocale(locale: string): Promise<void> {
    if (this.localeLoaded) return;
    try {
      const language = this.ewebinar.viewerRoomSettings.language || 'en';
      this.locale = getLocaleForLang(language, locale);
      this.i18n = await getLocalI18n(language);
      this.localeLoaded = true;
    } catch (error) {
      logger.error('setupLocale failed: ', error);
    }
  }

  /**
   * Hours of operations only apply for JIT and OD session in a fixed time zone or the attendee's local time zone.
   * If the fixed time zone for hours of operations has been specified, the hours of operations will apply for all session types (excluding replays)
   * @param session
   * @param isScheduled
   * @returns
   */
  private inHoursOfOperations(session: EDate, isScheduled?: boolean): boolean {
    if (
      this.scheduleSettings &&
      this.scheduleSettings.justInTimeHoursOfOperations &&
      this.scheduleSettings.justInTimeHoursOfOperations.length > 0
    ) {
      if (this.scheduleSettings?.hoursOfOperationTimezone) {
        session = session.tz(this.scheduleSettings.hoursOfOperationTimezone);
      } else if (isScheduled) {
        // when hoursOfOperationTimezone is turn off, the recurring schedules always appears
        return true;
      }
      const minutes: number = session.getHours() * 60 + session.getMinutes();
      const justInTimeWeekDay = session.formatWeekday().toLowerCase(); // mon-sun

      const hoursToday = this.scheduleSettings.justInTimeHoursOfOperations.find(
        ({ day }) => String(day) === justInTimeWeekDay
      );

      if (!hoursToday) {
        // Day not listed at all
        logger.debug('SESSIONS', 'SESSION day not listed');
        return false;
      }

      const [startMinutes, endMinutes] = hoursToday.times;
      if (minutes < startMinutes || minutes > endMinutes) {
        logger.debug('SESSIONS', 'SESSION not between start and end');
        return false;
      }
    }

    return true;
  }

  // TODO: Return next valid time and loop through until we find one
  private isValidJustInTime(justInTime: EDate, isOnDemand?: boolean): boolean {
    // Must not be in the past but RIGHT NOW is ok

    const now = this.nowEDate().setSeconds(0);
    if (
      !isOnDemand &&
      this.ewebinar.scheduleSettings.justInTimeIntervalMinutes !== 0 &&
      now.isAfter(justInTime)
    ) {
      logger.debug('JIT', 'JIT in the past 125');
      return false;
    }

    // Must not be before 1st session of startDate
    if (this.startTime.isAfter(justInTime)) {
      logger.debug('JIT', 'JIT before start 131');
      return false;
    }

    // Must not be after endDate
    if (this.endTime.isBefore(justInTime)) {
      logger.debug('JIT', 'JIT after end 137');
      return false;
    }

    // must be in hours of operation
    if (!this.inHoursOfOperations(justInTime)) {
      logger.debug('JIT', 'JIT not in hours of operation');
      return false;
    }

    return true;
  }

  private getJustInTimeSession(justInTimeDate?: EDate): EDate | null {
    if (
      !this.ewebinar.justInTimeModeEnabled ||
      !this.scheduleSettings ||
      !this.scheduleSettings.justInTimeIntervalMinutes // justInTimeIntervalMinutes: null || undefined || 0
    ) {
      logger.debug('JIT', 'JIT is not enabled');
      return null;
    }

    const { justInTimeIntervalMinutes } = this.scheduleSettings;

    let justInTimeSession = justInTimeDate;
    if (!justInTimeSession) {
      const now = this.nowEDate().setSeconds(0);
      const nextSessionMinutes =
        Math.ceil(now.getMinutes() / justInTimeIntervalMinutes) * justInTimeIntervalMinutes;
      justInTimeSession = now.setMinutes(nextSessionMinutes);
    }

    if (this.isValidJustInTime(justInTimeSession) && !this.inBlackoutRanges(justInTimeSession)) {
      return justInTimeSession;
    }
    return null;
  }

  private getOndemandSession(): EDate | null {
    if (!this.scheduleSettings || !this.scheduleSettings.showOndemandSession) {
      logger.debug('JIT', 'On-demand is not enabled');
      return null;
    }

    const now = this.nowEDate().setSeconds(0);
    const nextSessionMinutes = now.getMinutes();
    const ondemandSession = now.setMinutes(nextSessionMinutes);

    if (this.isValidJustInTime(ondemandSession, true) && !this.inBlackoutRanges(ondemandSession)) {
      return ondemandSession;
    }
    return null;
  }

  private inBlackout(blackout: BlackoutPeriod, session: EDate): boolean {
    const { startDay, endDay } = blackout;
    if (!startDay || !endDay) {
      console.error('No startDate or endDate in blackout ', { blackout });
      return false;
    }

    const sessionDay = (session.getMonth() + 1) * 31 + session.getDate();
    if (endDay >= startDay) {
      return startDay <= sessionDay && sessionDay <= endDay;
    }

    return sessionDay >= startDay || sessionDay <= endDay;
  }

  private inHoliday(holiday: BlackoutHoliday, session: EDate): boolean {
    if (!holiday.startDate || !holiday.endDate) {
      console.error('No startDate or endDate in holiday ', { holiday });
      return false;
    }

    const sessionYear = session.getFullYear();
    if (sessionYear !== holiday.year) {
      return false;
    }

    // We are just comparing times against each other - no timezone needed
    const sessionTime = session.toTime();
    if (
      new Date(holiday.startDate).getTime() <= sessionTime &&
      sessionTime <= new Date(holiday.endDate).getTime()
    ) {
      return true;
    }

    return false;
  }

  public inBlackoutRanges(session: EDate): boolean {
    let blackoutPeriods = this.scheduleSettings.blackoutPeriods;
    if (!blackoutPeriods) {
      return false;
    }

    // Check Holidays
    const inHoliday =
      this.scheduleSettings.blackoutHolidays &&
      this.scheduleSettings.blackoutHolidays.some((holiday: BlackoutHoliday) =>
        this.inHoliday(holiday, session)
      );
    if (inHoliday) return true;

    // Filter out Holidays to check date ranges
    blackoutPeriods = blackoutPeriods.filter(({ rule }) => !rule);
    return blackoutPeriods.some((blackout: BlackoutPeriod) => this.inBlackout(blackout, session));
  }

  /**
   * Calc scheduled sessions: Specific Dates & Times || One-time event
   * startDate is the first date and endDate is the last of the specific dates. This has been checked before saved
   * @returns
   */
  private calcSessionsForDates(): EDate[] {
    if (!this.schedules) return [];

    const sessions: EDate[] = [];
    const numberOfSessions = nullableCheck(this.scheduleSettings.numberOfSessions, 1);

    const now = this.nowEDate().setSeconds(0);
    if (this.endTime.isBefore(now)) return [];

    for (const schedule of this.schedules) {
      if (sessions.length > numberOfSessions) break;
      if (!schedule.atDate || !schedule.atMinutes || schedule.atMinutes.length === 0) {
        continue;
      }
      const d = schedule.atDate;
      const atSortedMinutes = [...schedule.atMinutes].sort((a, b) => a - b);
      for (const atMinutes of atSortedMinutes) {
        logger.debug('SESSIONS', `Scheduled session for specific dates -> ${d.toString()}`);

        const session = d
          .setHours(Math.floor(atMinutes / 60))
          .setMinutes(Math.floor(atMinutes % 60));

        // session is existing in the list
        if (sessions.find((s) => s.date.valueOf() === session.date.valueOf())) {
          continue;
        }
        // 1st session can be before NOW (allows register within X mins)
        if (
          sessions.length === 0 &&
          session.isBefore(this.nowEDate().addMinutes(ALLOW_REGISTER_WITHIN_MINUTES))
        ) {
          continue;
        }
        if (sessions.length > 0 && session.isBefore(now)) continue;

        // filter passed or blackout session
        if (!this.inBlackoutRanges(session)) {
          sessions.push(session);
        }
      }
    }

    return sessions;
  }

  private calcScheduledSessions(): EDate[] {
    if (!this.schedules) return [];

    const sessions: EDate[] = [];
    const numberOfSessions = nullableCheck(this.scheduleSettings.numberOfSessions, 1);

    const now = this.nowEDate().setSeconds(0);
    if (this.endTime.isBefore(now)) return [];

    // calc sessions for the Specific Dates and Times or One-time event
    if ([ScheduleTypes.SpecificDateTimes, ScheduleTypes.OneTime].includes(this.scheduleType)) {
      return this.calcSessionsForDates();
    }

    // calc sessions for Recurring

    const webinarStartTime = this.getWebinarStartTime();
    let d = this.startTime;
    // Only calculate for current year and next year to avoid infinite loop if there is no available session
    while (sessions.length < numberOfSessions && d.isSameOrBefore(this.endTime)) {
      // Convert mon = 0, sun = 6;
      const weekday = d.getDay();

      let atSortedMinutes: number[] = [];

      // 1. Check day valid and combine atMinutes in a day
      for (const schedule of this.schedules) {
        const onWeekDays = schedule.onWeekDays || [];

        if (!schedule.atMinutes || schedule.atMinutes.length === 0) {
          continue;
        }
        if (onWeekDays[weekday]) {
          // Recurring types:
          // 1.1 OnceAMonth
          if (schedule.recurringType === RecurringTypes.OnceAMonth && schedule.nthDayOfMonth) {
            // convert First = 0, Last = 4
            const nthDayOfmonth = dayOfMonthToNumber(schedule.nthDayOfMonth);
            const date = d.getNthDayOfMonth(nthDayOfmonth, weekday);
            if (!date || !d.isSameDay(date)) continue;
          }
          // 1.2 EveryTwoWeek1: Every 2 weeks (odd)
          if (schedule.recurringType === RecurringTypes.EveryTwoWeek1) {
            const isEveryTwoWeek1 = d.isValidEveryTwoWeeks(webinarStartTime);
            if (!isEveryTwoWeek1) continue;
          }
          // 1.3 EveryTwoWeek2: Every 2 weeks (even)
          if (schedule.recurringType === RecurringTypes.EveryTwoWeek2) {
            const isEveryTwoWeek2 = d.isValidEveryTwoWeeks(webinarStartTime.addDays(7));
            if (!isEveryTwoWeek2) continue;
          }

          // 1.4 SpecificDate
          if (schedule.recurringType === RecurringTypes.SpecificDate) {
            // compare specific date with current date "d"
            if (!schedule.atDate || !d.isSameDay(schedule.atDate)) {
              continue;
            }
          }

          // 1.5 The day is valid for other recurring types: everyday, weekdays, individual
          // 1.6 Combine atMinutes
          atSortedMinutes = [...new Set([...atSortedMinutes, ...schedule.atMinutes])];
        }
      }

      // 2. sort atMinutes in a day
      atSortedMinutes = atSortedMinutes.sort((a, b) => a - b);

      // 3. calc the sessions from atSortedMinutes of the day
      for (const atMinutes of atSortedMinutes) {
        // re-set the timezone and use set h-m-s instead of startOf('day') to make time correct after across the DST

        logger.debug('SESSIONS', `Scheduled session -> ${d.toString()}`);

        // setHours(), setMinutes() updates local timezone
        // (eg. 00:00 in London is 7:00am in Bangkok)
        // so we need to keep the local time offset
        const session = d
          .setHours(Math.floor(atMinutes / 60))
          .setMinutes(Math.floor(atMinutes % 60));

        // session is existing in the list
        if (sessions.find((s) => s.date.valueOf() === session.date.valueOf())) {
          continue;
        }
        // 1st session can be before NOW (allows register within X mins)
        if (
          sessions.length === 0 &&
          session.isBefore(this.nowEDate().addMinutes(ALLOW_REGISTER_WITHIN_MINUTES))
        ) {
          continue;
        }
        if (sessions.length > 0 && session.isBefore(now)) continue;

        // filter passed or blackout session
        if (!this.inBlackoutRanges(session) && this.inHoursOfOperations(session, true)) {
          sessions.push(session);
        }
      }

      const nextD = d.addDays(1);
      if (nextD.getTime() === d.getTime()) {
        throw new Error(`Error in addDays ${d.date.toString()} ${d.timezone}`);
      }
      d = nextD;
    }

    return sessions;
  }

  private isTeamFull(): boolean {
    return Boolean(this.ewebinar.fullSessions?.find((s) => s.status === SessionStatus.TeamFull));
  }

  private getSessionStatus(sessionTime: EDate): SessionStatus {
    if (this.isTeamFull()) return SessionStatus.Full;
    const status =
      this.ewebinar.fullSessions?.find(({ session }) => {
        return sessionTime.date.getTime() === new Date(session).getTime();
      })?.status ?? SessionStatus.Available;

    return status;
  }

  public async getEwebinarSessions(
    localeOverride?: string | null,
    skipFromNow?: boolean,
    justInTimeDate?: EDate,
    justInTimeName?: string
  ): Promise<EwebinarSessions> {
    if (!this.localeLoaded) {
      const locale = getLocaleForLang(
        this.ewebinar.viewerRoomSettings.language ?? 'en',
        localeOverride ?? getLocale()
      );
      await this.setupLocale(locale);
    }
    const { startDate } = this.ewebinar;

    const payload: EwebinarSessions = {
      sessions: [],
      timeZone: this.webinarTimezone,
    };

    if (!startDate) {
      return payload;
    }

    let sessionDates: EDate[] = [];
    let sessions: Session[] = [];

    // Scheduled sessions
    sessionDates = this.calcScheduledSessions();

    sessions = await Promise.all(
      sessionDates.map<Promise<Session>>(async (d): Promise<Session> => {
        return this.formatSession({
          dateTime: d.tz(this.clientTimezone),
          type: SessionType.Scheduled,
          status: this.getSessionStatus(d),
          skipFromNow,
          serverDateNow: this.serverDateNow,
        });
      })
    );
    // Limit the number of sessions will exclude JIT, On-demand, and Replay session
    if (sessions && sessions.length > 0) {
      sessions = sessions.splice(0, nullableCheck(this.scheduleSettings.numberOfSessions, 1));
    }

    // On-demand session
    const ondemand = this.getOndemandSession();

    // JIT
    const jit = this.getJustInTimeSession(justInTimeDate);

    if (
      jit &&
      (!ondemand || !jit.isEqual(ondemand)) &&
      (sessions.length === 0 ||
        (sessions.length > 0 && jit.isBefore(new EDate(sessions[0].date, this.webinarTimezone))))
    ) {
      logger.debug('JIT', `JIT -> ${jit.toString()}`);
      const jitStatus = this.getSessionStatus(jit);
      sessions = [
        await this.formatSession({
          dateTime: jit.tz(this.clientTimezone),
          type: SessionType.JustInTime,
          status: jitStatus,
          skipFromNow,
          skipToday: false,
          justInTimeName,
          serverDateNow: this.serverDateNow,
          isOnDemand: false,
        }),
        ...sessions,
      ];
    }

    // Add On-demand session to list
    if (ondemand) {
      logger.debug('JIT', `On-demand -> ${ondemand.toString()}`);
      const ondemandStatus = this.getSessionStatus(ondemand);
      sessions = [
        await this.formatSession({
          dateTime: ondemand.tz(this.clientTimezone),
          type: SessionType.JustInTime,
          status: ondemandStatus,
          skipFromNow,
          skipToday: false,
          serverDateNow: this.serverDateNow,
          isOnDemand: true,
        }),
        ...sessions,
      ];
    }

    // Replay
    const webinarStartTime = this.getWebinarStartTime();
    const now = this.nowEDate().setSeconds(0);
    if (this.scheduleSettings.showReplaySession && webinarStartTime.isBefore(now)) {
      const replay: Session = {
        type: SessionType.Replay,
        date: this.now(),
        value: 'replay',
        text: this.i18n!._(
          /*i18n*/ {
            id: 'Watch a replay',
          }
        ),
        status: this.isTeamFull() ? SessionStatus.Full : SessionStatus.Available,
      };

      sessions = [replay, ...sessions];
    }

    return {
      ...payload,
      sessions,
      timeZone: this.clientTimezone,
    };
  }

  public static async formatSessionTime({
    locale,
    date,
    i18n,
    skipFromNow,
    isOnDemandSession,
    skipToday,
    justInTimeName,
    status,
    serverDateNow,
    isTeamFull,
  }: {
    locale: string;
    date: EDate;
    i18n: I18n;
    skipFromNow?: boolean;
    isOnDemandSession?: boolean;
    skipToday?: boolean;
    justInTimeName?: string;
    status?: SessionStatus;
    serverDateNow?: ServerDateNow;
    isTeamFull?: boolean;
  }): Promise<Omit<Session, 'type' | 'isOnDemand'>> {
    const now = new EDate(serverDateNow?.() ?? new Date(Date.now()), date.timezone);
    const minutesFromNow = date.diffInMinutes(now);

    let text = '';
    let at = i18n._('@');
    let dateFormat = 'PP';
    if (locale.startsWith('ar')) {
      // Customer states that for Arabi we should show DD/MM/YYYY not month hname
      dateFormat = 'P';
      at = 'على الساعة';
    }
    let fmt = `eeee, ${dateFormat} '${at}' p zzz`;

    let isToday = false;
    if (!skipToday && now.format('MMddyyyy') === date.format('MMddyyyy')) {
      fmt = 'p zzz';
      isToday = true;
    }

    if (justInTimeName) {
      text = i18n._(justInTimeName);
    } else if (isOnDemandSession) {
      text = i18n._(/*i18n*/ { id: 'Watch now on demand' });
    } else if (!skipFromNow && minutesFromNow <= 60) {
      if (minutesFromNow > 0) {
        text = `${await date.formatAsync(fmt, locale)} ${i18n._(
          /*i18n*/ {
            id: '{minutesFromNow, plural, one {(in # min)} other {(in # min)}}',
            values: {
              minutesFromNow,
            },
          }
        )}`;
      } else {
        // text = i18n._(/*i18n*/ { id: 'Starts now' });
        text = await date.formatAsync(fmt, locale);
      }
    } else {
      text = await date.formatAsync(fmt, locale, date.isSameYear(now));
    }

    if (isToday && !isOnDemandSession && !justInTimeName) {
      if (minutesFromNow > 0) {
        text = `${i18n._(/*i18n*/ { id: 'TODAY' })} ${at} ${text}`;
      } else {
        // show "Live now" if session started X mins ago
        text = `${text} (${i18n._(/*i18n*/ { id: 'Live now' })})`;
      }
    }

    if (status === SessionStatus.Full && !isTeamFull) {
      text = `${text} - ${i18n._(/*i18n*/ { id: 'Session full' })} -`;
    }

    return {
      date: date.date,
      value: date.toISOString(),
      text: upperFirst(text),
      status: SessionStatus.Available,
    };
  }

  public async formatSession({
    dateTime,
    type,
    status,
    skipFromNow,
    skipToday,
    justInTimeName,
    serverDateNow,
    isOnDemand,
  }: {
    dateTime: EDate;
    type: SessionType;
    status: SessionStatus;
    skipFromNow?: boolean;
    skipToday?: boolean;
    justInTimeName?: string;
    serverDateNow?: ServerDateNow;
    isOnDemand?: boolean;
  }): Promise<Session> {
    const { date, text, value } = await SessionCalculator.formatSessionTime({
      locale: this.locale!,
      date: dateTime,
      i18n: this.i18n!,
      skipFromNow,
      isOnDemandSession: isOnDemand,
      skipToday,
      justInTimeName,
      status,
      serverDateNow,
      isTeamFull: this.isTeamFull(),
    });

    return {
      type,
      date: date,
      isOnDemand,
      text,
      value,
      status,
    };
  }
}
